import {fromCognitoIdentityPool} from '@aws-sdk/credential-providers'
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  type FetchUserAttributesOutput,
  resetPassword,
  signIn,
  signOut,
} from 'aws-amplify/auth'
import {UserManager, UserManagerSettings, WebStorageStateStore} from 'oidc-client-ts'

import {base64urlDecode, isTokenValid} from '@/router/token-utils'

import {clearAllAuthData} from './auth-cleanup'

export interface AuthSession {
  isAuthenticated: boolean
  authMethod: 'UserPool' | 'OIDC' | null
  tokens?: any
  credentials?: {
    accessKeyId: string
    secretAccessKey: string
    sessionToken?: string
    expiration?: Date
  }
}

export interface SignInResult {
  isSignedIn: boolean
  nextStep?: any
}

interface GetCredentialsOptions {
  forceRefresh?: boolean
}

interface CredentialsAndIdentityId {
  credentials: {
    accessKeyId: string
    secretAccessKey: string
    sessionToken?: string
    expiration?: Date
  }
  identityId?: string
}

/**
 * Unified authentication adapter that handles both Cognito User Pool and OIDC authentication
 * Provides a single interface for all authentication operations
 */
export class AuthAdapter {
  private identityPoolId: string
  private providerDomain: string
  private cachedCredentials: CredentialsAndIdentityId | null = null
  private cachedForToken: string | null = null
  private cacheExpiry: Date | null = null
  private sessionCallCount = 0
  private cachedSession: AuthSession | null = null
  private sessionCacheTime = 0

  // OIDC-related properties
  private _userManager: UserManager | null = null
  private readonly clientId: string
  private readonly authority: string

  constructor() {
    this.identityPoolId = process.env.VUE_APP_COGNITO_IDENTITY_POOL_ID!
    // Remove protocol from URL for AWS Identity Pool provider key
    this.providerDomain = process.env.VUE_APP_PANEX_AUTHORITY!.replace(/^https?:\/\//, '')

    // Initialize OIDC client configuration
    this.clientId = process.env.VUE_APP_PANEX_CLIENT_ID!
    this.authority = process.env.VUE_APP_PANEX_AUTHORITY!
  }

  /**
   * Sign in with User Pool credentials (Cognito username/password)
   * @param email - Email for User Pool authentication
   * @param password - Password for User Pool authentication
   * @returns SignInResult with authentication status
   */
  async signInWithCredentials(email: string, password: string): Promise<SignInResult> {
    const result = await signIn({username: email, password})
    return {
      isSignedIn: result.isSignedIn,
      nextStep: result.nextStep,
    }
  }

  /**
   * Confirm sign-in with new password (for NEW_PASSWORD_REQUIRED challenge)
   */
  async confirmSignInWithNewPassword(
    newPassword: string,
    userAttributes?: Record<string, string>,
  ): Promise<SignInResult> {
    const options = userAttributes ? {userAttributes} : undefined
    const result = await confirmSignIn({
      challengeResponse: newPassword,
      options,
    })
    return {
      isSignedIn: result.isSignedIn,
      nextStep: result.nextStep,
    }
  }

  /**
   * Reset User Pool password
   */
  async resetPassword(email: string) {
    return await resetPassword({
      username: email,
    })
  }

  /**
   * Confirm User Pool password reset
   */
  async confirmResetPassword(email: string, confirmationCode: string, newPassword: string) {
    return await confirmResetPassword({
      username: email,
      confirmationCode,
      newPassword,
    })
  }

  /**
   * Unified sign out method
   * Enhanced to clear all localStorage keys that can cause "Identity forbidden" errors
   */
  async signOut(): Promise<void> {
    try {
      // Stop OIDC silent renew and remove user BEFORE clearing storage.
      // This must happen first because:
      // 1. clearAllAuthData() removes OIDC keys from localStorage, which makes
      //    hasActiveOIDCSession() return false — skipping the cleanup entirely
      // 2. Without removeUser(), the UserManager's automaticSilentRenew timer
      //    keeps running and can re-create the OIDC session in localStorage
      //    after we clear it (Panex has no end_session_endpoint so the provider
      //    session stays alive and signinSilent succeeds)
      // 3. On next login (e.g. Cognito), fetchUserAttributes() would find the
      //    stale OIDC token and return the previous user's dealerId
      await this.stopOIDCUserManager()

      // Clear all authentication data from storage
      clearAllAuthData()

      // Clear cached credentials in memory
      this.cachedCredentials = null
      this.cachedForToken = null
      this.cacheExpiry = null
      this.cachedSession = null
      this.sessionCacheTime = 0

      // Sign out from Amplify User Pool (no-op if session was OIDC-only)
      try {
        await signOut()
      } catch {
        // Amplify signOut may fail if there was no User Pool session — safe to ignore
      }
    } catch (error) {
      console.error('Error during sign out:', error)
      clearAllAuthData()
      throw error
    }
  }

  /**
   * Stop the OIDC UserManager: revoke tokens, remove user, and stop silent renew.
   * Safe to call even if there is no active OIDC session.
   */
  private async stopOIDCUserManager(): Promise<void> {
    const userManager = this.createUserManager()

    userManager.stopSilentRenew()

    try {
      await userManager.revokeTokens()
    } catch {
      // Revocation is best-effort — provider may not support it
    }

    try {
      await userManager.removeUser()
    } catch {
      // removeUser clears the store and stops internal timers
    }
  }

  /**
   * Get current authentication session
   */
  async getCurrentSession(): Promise<AuthSession> {
    this.sessionCallCount++

    // Use cached session if recent (within 5 seconds) and token is still valid
    const now = Date.now()
    if (this.cachedSession && now - this.sessionCacheTime < 5000) {
      // Validate token hasn't expired within the cache window
      if (this.cachedSession.tokens?.idToken && isTokenValid(this.cachedSession.tokens.idToken)) {
        return this.cachedSession
      }
      // Token expired during cache window, invalidate cache
      this.cachedSession = null
    }

    if (await this.hasActiveOIDCSession()) {
      // Get OIDC credentials
      const oidcCredentials = await this.getCredentialsAndIdentityId()

      const session: AuthSession = {
        isAuthenticated: true,
        authMethod: 'OIDC' as const,
        tokens: {
          idToken: await this.getCurrentOIDCToken(),
          accessToken: await this.getCurrentOIDCAccessToken(),
        },
        credentials: oidcCredentials?.credentials,
      }

      // Cache the session
      this.cachedSession = session
      this.sessionCacheTime = Date.now()

      return session
    } else {
      const session = await fetchAuthSession()
      const isAuthenticated = !!session.tokens?.accessToken

      return {
        isAuthenticated,
        authMethod: isAuthenticated ? 'UserPool' : null,
        tokens: {
          idToken: session.tokens?.idToken,
          accessToken: session.tokens?.accessToken,
        },
        credentials: session.credentials,
      }
    }
  }

  /**
   * Get AWS credentials for making AWS service calls
   * @deprecated Use getCurrentSession() instead which includes both tokens and credentials
   */
  async getAWSCredentials(): Promise<CredentialsAndIdentityId | undefined> {
    const session = await this.getCurrentSession()

    if (!session.credentials) {
      return undefined
    }

    return {
      credentials: session.credentials,
      identityId: undefined, // Session doesn't expose identityId separately
    }
  }

  /**
   * Check if user is currently authenticated (either method)
   */
  async isAuthenticated(): Promise<boolean> {
    const session = await this.getCurrentSession()
    return session.isAuthenticated
  }

  /**
   * Get the current authentication method
   */
  async getAuthMethod(): Promise<'UserPool' | 'OIDC' | null> {
    const session = await this.getCurrentSession()
    return session.authMethod
  }

  /**
   * Check if current session is OIDC
   */
  async isOIDCSession(): Promise<boolean> {
    return await this.hasActiveOIDCSession()
  }

  /**
   * Check if current session is User Pool
   */
  async isUserPoolSession(): Promise<boolean> {
    if (await this.hasActiveOIDCSession()) return false

    try {
      const session = await fetchAuthSession()
      return !!session.tokens?.accessToken
    } catch {
      return false
    }
  }

  /**
   * Fetch user attributes using appropriate method (OIDC or User Pool)
   */
  async fetchUserAttributes(): Promise<FetchUserAttributesOutput> {
    try {
      // Check if we have an OIDC token in session
      const oidcToken = await this.getCurrentOIDCToken()

      if (oidcToken) {
        try {
          // Decode the ID token to extract user attributes (using UTF-8 safe decoder)
          const tokenPayload = JSON.parse(base64urlDecode(oidcToken.split('.')[1]))

          // Check if token is expired (with 5 minute grace period for clock skew)
          const now = Math.floor(Date.now() / 1000)
          if (tokenPayload.exp && now > tokenPayload.exp + 300) {
            await this.clearOIDCSession()
            throw new Error('OIDC token expired')
          }

          // Map ID token claims to Amplify user attributes format
          const userAttributes: FetchUserAttributesOutput = {
            sub: tokenPayload.sub || 'oidc-user',
            email: tokenPayload.email || '',
            email_verified: tokenPayload.email_verified?.toString() || 'false',
            given_name: tokenPayload.given_name || '',
            family_name: tokenPayload.family_name || '',
            preferred_username: tokenPayload.preferred_username || tokenPayload.email || '',
            panex_id: tokenPayload.PanexID || '',
          }
          userAttributes['name'] = tokenPayload.name || ''
          userAttributes['displayName'] = tokenPayload.displayName || tokenPayload.name || ''

          // Add custom attributes from token (undefined values are fine)
          userAttributes['custom:dealerId'] = tokenPayload['custom:dealerId']
          userAttributes['custom:legacy_dealer_id'] = tokenPayload['custom:legacy_dealer_id']
          userAttributes['custom:external_id'] = tokenPayload['custom:external_id']
          userAttributes['custom:honda_plus_groups'] = tokenPayload['custom:honda_plus_groups']
          userAttributes['cognito:groups'] = Array.isArray(tokenPayload['cognito:groups'])
            ? tokenPayload['cognito:groups'].join(',')
            : tokenPayload['cognito:groups']
          userAttributes['cognito:username'] = tokenPayload['cognito:username']

          return userAttributes
        } catch (error) {
          // Don't clear session on decoding errors - might be temporary
          // Only re-throw definitive expiry errors
          if (error instanceof Error && error.message === 'OIDC token expired') {
            throw error
          }
          throw new Error('Invalid OIDC token format')
        }
      }

      // No OIDC token, use Amplify's fetchUserAttributes for User Pool authentication
      return await fetchUserAttributes()
    } catch (error) {
      console.error('Error fetching user attributes:', error)
      throw error
    }
  }

  /**
   * Get AWS credentials using OIDC token from Panex
   * Returns undefined if no OIDC token exists, allowing fallback to User Pool auth
   * Throws error if OIDC token exists but credential exchange fails
   */
  private async getCredentialsAndIdentityId(
    options?: GetCredentialsOptions,
  ): Promise<CredentialsAndIdentityId | undefined> {
    const oidcToken = await this.getCurrentOIDCToken()

    if (!oidcToken) {
      return undefined // Graceful fallback to User Pool authentication
    }

    // Check if we can use cached credentials
    if (!options?.forceRefresh && this.cachedCredentials && this.cachedForToken === oidcToken) {
      const now = new Date()

      // Check if cached credentials are still valid (with 5 minute buffer)
      if (this.cacheExpiry && now < new Date(this.cacheExpiry.getTime() - 5 * 60 * 1000)) {
        return this.cachedCredentials
      }
    }

    try {
      // Exchange OIDC token for AWS credentials via Identity Pool
      const logins = {
        [this.providerDomain]: oidcToken,
      }

      const credentialsProvider = fromCognitoIdentityPool({
        clientConfig: {
          region: process.env.VUE_APP_AWS_REGION!,
        },
        identityPoolId: this.identityPoolId,
        logins: logins,
      })

      const awsCredentials = await credentialsProvider()

      const credentials = {
        credentials: {
          accessKeyId: awsCredentials.accessKeyId,
          secretAccessKey: awsCredentials.secretAccessKey,
          sessionToken: awsCredentials.sessionToken,
          expiration: awsCredentials.expiration,
        },
        identityId: undefined, // Will be populated by AWS SDK if needed
      }

      // Cache the credentials
      this.cachedCredentials = credentials
      this.cachedForToken = oidcToken
      this.cacheExpiry = awsCredentials.expiration || new Date(Date.now() + 60 * 60 * 1000) // Default 1 hour

      return credentials
    } catch (error: any) {
      // If OIDC token exists but credential exchange fails, throw error
      throw new Error(`OIDC credential exchange failed: ${error.message}`)
    }
  }

  /**
   * Clear cached credentials and OIDC token
   */
  private async clearCredentialsAndIdentityId(): Promise<void> {
    this.cachedCredentials = null
    this.cachedForToken = null
    this.cacheExpiry = null
  }

  // ================================
  // OIDC Methods - OpenID Connect authentication using oidc-client-ts
  // ================================

  /**
   * Get UserManager settings
   */
  private getUserManagerSettings(): UserManagerSettings {
    return {
      authority: this.authority,
      client_id: this.clientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: window.location.origin,
      scope: 'openid email profile phone aws.cognito.signin.user.admin',
      response_type: 'code',
      loadUserInfo: true,
      userStore: new WebStorageStateStore({store: window.localStorage}),
      revokeTokensOnSignout: true, // Enable automatic token revocation
      extraQueryParams: {},
      // IMPORTANT: oidc-client-ts has automaticSilentRenew = true by default!
      // This means tokens ARE being automatically renewed in the background.
      // To disable auto-renewal, uncomment the following line:
      // automaticSilentRenew: false,
    }
  }

  /**
   * Create UserManager with consistent settings (singleton)
   */
  private createUserManager(): UserManager {
    if (!this._userManager) {
      this._userManager = new UserManager(this.getUserManagerSettings())
    }
    return this._userManager
  }

  /**
   * Sign in with OIDC (redirects to external provider)
   */
  async signInWithOIDC(): Promise<void> {
    const userManager = this.createUserManager()
    await userManager.clearStaleState()
    await userManager.signinRedirect()
  }

  /**
   * Initiate OIDC sign-out redirect
   */
  async initiateSignOut(): Promise<void> {
    // Clear auth-adapter cache
    this.cachedSession = null
    this.sessionCacheTime = 0

    // Clear AWS credentials
    await this.clearCredentialsAndIdentityId()

    // Since Panex doesn't provide end_session_endpoint, do local cleanup only
    await this.stopOIDCUserManager()
  }

  /**
   * Handle OIDC callback
   */
  async handleCallback(): Promise<any> {
    const userManager = this.createUserManager()
    const user = await userManager.signinRedirectCallback()

    if (!user) {
      throw new Error('No user returned from OIDC callback')
    }

    if (!user.id_token) {
      throw new Error('No ID token received from OIDC provider')
    }

    return user
  }

  /**
   * Helper function to check if user has active OIDC session
   */
  async hasActiveOIDCSession(): Promise<boolean> {
    const userManager = this.createUserManager()
    const user = await userManager.getUser()
    return !!user && !user.expired
  }

  /**
   * Helper function to get current OIDC user
   */
  async getCurrentOIDCUser() {
    const userManager = this.createUserManager()
    return await userManager.getUser()
  }

  /**
   * Helper function to get current OIDC ID token
   */
  async getCurrentOIDCToken(): Promise<string | null> {
    const user = await this.getCurrentOIDCUser()
    return user?.id_token || null
  }

  /**
   * Helper function to get current OIDC access token
   */
  async getCurrentOIDCAccessToken(): Promise<string | null> {
    const user = await this.getCurrentOIDCUser()
    return user?.access_token || null
  }

  /**
   * Clear OIDC session (for internal use only)
   */
  private async clearOIDCSession(): Promise<void> {
    await this.stopOIDCUserManager()
  }
}

// Export singleton instance
export const authAdapter = new AuthAdapter()
